import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './components/Login';
import ChatInterface from './components/ChatInterface';
import MetadataManager from './components/MetadataManager';
import UserManager from './components/UserManager';
import Dashboard from './components/Dashboard';
import Hub from './components/Hub';
import MultifittingDashboard from './components/MultifittingDashboard';
import './components/MobileStyles.css'; // Import responsive styles

// --- MVP 2026: Self-Assessment e Knowledge Management (Specifica tecnica v1.1) ---
// In modalità demo (MOCK_MODE, src/api/config.js) finché i gate G01/G03 non sono
// chiusi da Monaco. Il KM non introduce una seconda autenticazione (Specifica §3):
// la rotta /knowledge-management riusa il ProtectedRoute e l'AuthContext esistenti;
// /self-assessment resta pubblica come richiesto dalla Specifica §4.1.
import { AuthProvider as KmRoleProvider } from './lib/auth.jsx';
import SelfAssessmentPage from './features/self-assessment/SelfAssessmentPage.jsx';
import KmLayout from './features/knowledge-management/KmLayout.jsx';
import EsperienzeListPage from './features/knowledge-management/pages/EsperienzeListPage.jsx';
import EsperienzaDetailPage from './features/knowledge-management/pages/EsperienzaDetailPage.jsx';
import EsperienzaOpenPage from './features/knowledge-management/pages/EsperienzaOpenPage.jsx';
import RicercaPage from './features/knowledge-management/pages/RicercaPage.jsx';
import SchedaDetailPage from './features/knowledge-management/pages/SchedaDetailPage.jsx';
import DashboardPage from './features/knowledge-management/pages/DashboardPage.jsx';
import QuestionariEditorPage from './features/knowledge-management/pages/QuestionariEditorPage.jsx';
import HrSelfAssessmentStatusPage from './components/km/HrSelfAssessmentStatusPage.jsx';
import EsperienzeInScadenzaPage from './components/km/EsperienzeInScadenzaPage.jsx';
import SaLabPage from './features/sa-lab/SaLabPage.jsx';

const ProtectedRoute = ({ children }) => {
    const { token, loading } = useAuth();
    if (loading) return <div>Loading...</div>;
    if (!token) return <Navigate to="/login" />;
    return children;
};

function App() {
    return (
        <AuthProvider>
            <Router>
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/" element={
                        <ProtectedRoute>
                            <Hub />
                        </ProtectedRoute>
                    } />
                    <Route path="/chat" element={
                        <ProtectedRoute>
                            <ChatInterface />
                        </ProtectedRoute>
                    } />
                    <Route path="/metadata" element={
                        <ProtectedRoute>
                            <MetadataManager />
                        </ProtectedRoute>
                    } />
                    <Route path="/users" element={
                        <ProtectedRoute>
                            <UserManager />
                        </ProtectedRoute>
                    } />
                    <Route path="/dashboard" element={
                        <ProtectedRoute>
                            <Dashboard />
                        </ProtectedRoute>
                    } />
                    <Route path="/dashboard-multifitting" element={
                        <ProtectedRoute>
                            <MultifittingDashboard />
                        </ProtectedRoute>
                    } />

                    {/* MVP1 — Self-Assessment: rotta pubblica, nessun JWT (Specifica v1.1 §4.1).
                        KmRoleProvider è necessario perché useKmClient() lo richiede internamente
                        (src/lib/useKmClient.js importa useAuth da lib/auth.jsx) — qui non ha alcun
                        ruolo di autenticazione, la rotta resta pubblica. */}
                    <Route
                        path="/self-assessment"
                        element={
                            <KmRoleProvider>
                                <SelfAssessmentPage />
                            </KmRoleProvider>
                        }
                    />

                    {/* Back-office Self-Assessment: vista generale per HR, protetta come il
                        resto del portale — a differenza di /self-assessment (pubblica, sopra),
                        non usa KmRoleProvider perché useRealKmClient() prende il token dal vero
                        AuthContext, non dal simulatore di ruolo. */}
                    <Route
                        path="/self-assessment/console"
                        element={
                            <ProtectedRoute>
                                <HrSelfAssessmentStatusPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* SA Lab: banco di prova interno del SA conversazionale in
                        streaming (vLLM), protetto come la console HR. */}
                    <Route path="/sa-lab" element={<ProtectedRoute><SaLabPage /></ProtectedRoute>} />

                    <Route
                        path="/knowledge-management/esperienze-in-scadenza"
                        element={
                            <ProtectedRoute>
                                <EsperienzeInScadenzaPage />
                            </ProtectedRoute>
                        }
                    />

                    {/* MVP2 — Knowledge Management: protetta dal login TalentGPT esistente.
                        KmRoleProvider fornisce solo il selettore di ruolo per il collaudo
                        ACL in demo (HR/Sales/Management/Admin), non gestisce token o sessione. */}
                    <Route
                        path="/knowledge-management"
                        element={
                            <ProtectedRoute>
                                <KmRoleProvider>
                                    <KmLayout />
                                </KmRoleProvider>
                            </ProtectedRoute>
                        }
                    >
                        <Route index element={<Navigate to="esperienze" replace />} />
                        <Route path="esperienze" element={<EsperienzeListPage />} />
                        <Route path="esperienze/nuova" element={<EsperienzaOpenPage />} />
                        <Route path="esperienze/:esperienzaId" element={<EsperienzaDetailPage />} />
                        <Route path="ricerca" element={<RicercaPage />} />
                        <Route path="dashboard" element={<DashboardPage />} />
                        <Route path="schede/:schedaId" element={<SchedaDetailPage />} />
                        <Route path="questionari" element={<QuestionariEditorPage />} />
                    </Route>
                </Routes>
            </Router>
        </AuthProvider>
    );
}

export default App;
